import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useFirestore } from '../../hooks/useFirestore';
import { getHeroContent } from '../../firebase/firestore';
import Button from '../common/Button';
import Loading from '../common/Loading';

// Import images
import hero1 from '../../assets/images/hero-1.png';
import hero2 from '../../assets/images/hero-2.png';
import hero3 from '../../assets/images/hero-3.png';

import './Hero.css';

const Hero = () => {
    const { t, i18n } = useTranslation();
    const { data: heroContent, loading } = useFirestore(useCallback(() => getHeroContent(), []));
    const [currentSlide, setCurrentSlide] = useState(0);

    const slides = [
        {
            id: 1,
            image: hero1,
            titleAr: heroContent?.titleAr || t('hero.title'),
            titleEn: heroContent?.titleEn || t('hero.title'),
            subtitleAr: heroContent?.subtitleAr || t('hero.subtitle'),
            subtitleEn: heroContent?.subtitleEn || t('hero.subtitle'),
        },
        {
            id: 2,
            image: hero2,
            titleAr: heroContent?.slide2TitleAr || "جودة لا تضاهى",
            titleEn: heroContent?.slide2TitleEn || "Unmatched Quality",
            subtitleAr: heroContent?.slide2SubtitleAr || "أحدث التقنيات العالمية في عالم المصاعد والسلالم المتحركة",
            subtitleEn: heroContent?.slide2SubtitleEn || "The latest international technologies in elevators and escalators",
        },
        {
            id: 3,
            image: hero3,
            titleAr: heroContent?.slide3TitleAr || "أمان متناهي",
            titleEn: heroContent?.slide3TitleEn || "Ultimate Safety",
            subtitleAr: heroContent?.slide3SubtitleAr || "نلتزم بأعلى معايير السلامة والجودة لضمان راحتكم",
            subtitleEn: heroContent?.slide3SubtitleEn || "We are committed to the highest safety and quality standards to ensure your comfort",
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 6000);
        return () => clearInterval(timer);
    }, [slides.length]);

    if (loading) {
        return (
            <div className="hero-loading">
                <Loading message={t('common.loading')} />
            </div>
        );
    }

    const currentData = slides[currentSlide];
    const isAr = i18n.language === 'ar';

    return (
        <section className="hero">
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentSlide}
                    className="hero-slide"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                >
                    <div
                        className="hero-image"
                        style={{ backgroundImage: `url(${currentData.image})` }}
                    ></div>
                    <div className="hero-overlay"></div>
                </motion.div>
            </AnimatePresence>

            <div className="container">
                <div className="hero-content">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentSlide}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -30 }}
                            transition={{ duration: 0.8 }}
                        >
                            <motion.h1 className="hero-title">
                                {isAr ? currentData.titleAr : currentData.titleEn}
                            </motion.h1>

                            <motion.p className="hero-subtitle">
                                {isAr ? currentData.subtitleAr : currentData.subtitleEn}
                            </motion.p>

                            <div className="hero-cta">
                                <Button variant="primary" onClick={() => window.location.href = '/contact'}>
                                    {t('hero.cta')}
                                </Button>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="hero-indicators">
                    {slides.map((_, index) => (
                        <button
                            key={index}
                            className={`indicator ${index === currentSlide ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(index)}
                        ></button>
                    ))}
                </div>

                <motion.div
                    className="scroll-indicator"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.8 }}
                >
                    <ChevronDownIcon className="scroll-icon" />
                    <span>{t('hero.scrollDown')}</span>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;

